import { useCallback, useEffect, useRef } from 'react'
import {
  FieldValues,
  UseFormReset,
  UseFormWatch,
} from 'react-hook-form'
import { isEmptyFilterParams } from '../components/ListView/filterConfig'
import { normalizeFilterParams } from '../types/SelectItem'

interface UseFilterSyncOptions {
  initialFilterParams?: Record<string, unknown>
  reset: UseFormReset<FieldValues>
  watch: UseFormWatch<FieldValues>
  onSubmit: (data: FieldValues) => void
  getSyncKey: (normalized: Record<string, unknown> | null) => string | null
  getClearSyncKey: () => string | null
  shouldSkipSync?: () => boolean
  onEmptySync?: () => void
  onClear?: () => void
}

export function useFilterSync({
  initialFilterParams,
  reset,
  watch,
  onSubmit,
  getSyncKey,
  getClearSyncKey,
  shouldSkipSync,
  onEmptySync,
  onClear,
}: UseFilterSyncOptions) {
  const syncKeyRef = useRef<string | null>(null)
  const formStateRef = useRef<FieldValues>({})
  const isClearingRef = useRef(false)
  // When watch fires onSubmit, it changes initialFilterParams. skipNextSyncRef prevents
  // useFilterSync from re-applying that data back to the form (echo-back loop).
  // This also blocks re-applying stale values when hidden fields re-mount with old RHF state.
  const skipNextSyncRef = useRef(false)
  const getSyncKeyRef = useRef(getSyncKey)
  const getClearSyncKeyRef = useRef(getClearSyncKey)
  const shouldSkipSyncRef = useRef(shouldSkipSync)
  const onEmptySyncRef = useRef(onEmptySync)
  const onClearRef = useRef(onClear)

  getSyncKeyRef.current = getSyncKey
  getClearSyncKeyRef.current = getClearSyncKey
  shouldSkipSyncRef.current = shouldSkipSync
  onEmptySyncRef.current = onEmptySync
  onClearRef.current = onClear

  const resetSyncState = useCallback(() => {
    syncKeyRef.current = null
  }, [])

  useEffect(() => {
    if (initialFilterParams === undefined || initialFilterParams === null) return

    const isEmpty = isEmptyFilterParams(initialFilterParams)
    if (isEmpty) {
      skipNextSyncRef.current = false
      const emptyKey = getSyncKeyRef.current(null)
      if (emptyKey !== null && syncKeyRef.current === emptyKey) {
        isClearingRef.current = false
        onEmptySyncRef.current?.()
        return
      }
      if (emptyKey !== null) syncKeyRef.current = emptyKey
      reset({})
      formStateRef.current = {}
      isClearingRef.current = false
      onEmptySyncRef.current?.()
      return
    }

    // Skip re-applying params that came from our own watch-triggered submit.
    // This prevents stale RHF values (retained for unmounted fields) from being
    // re-applied when a hidden column is re-shown and its field re-mounts with old data.
    if (skipNextSyncRef.current) {
      skipNextSyncRef.current = false
      return
    }

    if (isClearingRef.current) return
    if (shouldSkipSyncRef.current?.()) return

    const normalized = normalizeFilterParams(
      initialFilterParams as Record<string, unknown>,
    )
    const key = getSyncKeyRef.current(normalized)
    if (key !== null && syncKeyRef.current === key) return
    if (key !== null) syncKeyRef.current = key
    reset(normalized)
    formStateRef.current = { ...normalized }
  }, [initialFilterParams, reset])

  const clearFilters = useCallback(() => {
    isClearingRef.current = true
    const clearKey = getClearSyncKeyRef.current()
    if (clearKey !== null) syncKeyRef.current = clearKey
    formStateRef.current = {}
    onSubmit({})
    reset({})
    onClearRef.current?.()
  }, [onSubmit, reset])

  const clearSpecificFields = useCallback((fieldNames: string[]) => {
    const updated = { ...formStateRef.current }
    fieldNames.forEach((f) => { updated[f] = null })
    formStateRef.current = updated
    onSubmit(updated)
  }, [onSubmit])

  useEffect(() => {
    const subscription = watch((data) => {
      formStateRef.current = { ...data }
      skipNextSyncRef.current = true
      onSubmit(data)
    })
    return () => subscription.unsubscribe()
  }, [onSubmit, watch])

  return {
    formStateRef,
    clearFilters,
    clearSpecificFields,
    resetSyncState,
  }
}
